import { InfoIcon } from 'lucide-react'

import { TextLink, Tooltip, TooltipProvider } from 'slab'
import { getFormattedDate } from 'utils'
import { useGetAdminEmployerIchraConfigsIdAllowanceStructure as useGetAllowanceSummary } from 'utils/web_api'
import { type AdminEmployerICHRAConfigAllowanceStructure } from 'utils/web_api.schemas'

function GranularAllowancesSummary ({ allowanceSummary } : { allowanceSummary: AdminEmployerICHRAConfigAllowanceStructure }) {
  return (
    <div className='overflow-x-auto overflow-y-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg mt-2'>
      <table className='table-auto w-full divide-y divide-gray-300' data-testid='granular_allowances'>
        <thead className='bg-gray-50'>
          <tr>
            <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
              <div className='flex justify-start'>
                Coverage %
              </div>
            </th>
            <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
              <div className='flex justify-start'>
                Benchmark
              </div>
            </th>
            {allowanceSummary.include_custom_columns && (
              <>
                <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
                  <div className='flex justify-start'>
                    Employment
                  </div>
                </th>
                <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
                  <div className='flex justify-start'>
                    Pay type
                  </div>
                </th>
                <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
                  <div className='flex justify-start'>
                    Location
                  </div>
                </th>
              </>
            )}
            <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
              <div className='flex justify-start'>
                Version
              </div>
            </th>
            <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
              <div className='flex justify-start'>
                QME Treatment
              </div>
            </th>
            {allowanceSummary.include_multiplier_columns && (
              <>
                <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
                  <div className='flex justify-start'>
                    Employee %
                  </div>
                </th>
                <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
                  <div className='flex justify-start'>
                    Spouse %
                  </div>
                </th>
                <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
                  <div className='flex justify-start'>
                    Dependent %
                  </div>
                </th>
              </>
            )}
            <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
              <div className='flex justify-start'>
                Updated at
              </div>
            </th>
          </tr>
        </thead>
        <tbody className='divide-y divide-gray-200 bg-white'>
          {allowanceSummary.granular_allowances && allowanceSummary.granular_allowances.map((allowance) => (
            <tr
              className='odd:bg-white even:bg-gray-50 hover:cursor-pointer' key={allowance.id} onClick={() => {
                window.open(
                  `/admin/inspect/${allowance.id}`,
                  '_blank',
                  'noopener noreferrer'
                )
              }}
            >
              <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                {allowance.coverage_percentile || 0}%
              </td>
              <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                {allowance.benchmark || '(not set)'}
              </td>
              {allowanceSummary.include_custom_columns && (
                <>
                  <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                    {allowance.employment_type || 'n/a'}
                  </td>
                  <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                    {allowance.pay_type || 'n/a'}
                  </td>
                  <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                    {allowance.location_id || 'n/a'}
                  </td>
                </>
              )}
              <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                {allowance.version || 0}
              </td>
              <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                {allowance.qme_treatment || '(not set)'}
              </td>
              {allowanceSummary.include_multiplier_columns && (
                <>
                  <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                    {allowance.granular_employee_multiplier || '(not set)'}%
                  </td>
                  <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                    {allowance.granular_spouse_multiplier || '(not set)'}%
                  </td>
                  <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                    <div className='flex gap-1 items-center'>
                      {allowance.granular_dependent_multiplier || '(not set)'}%
                      {allowance.granular_dependent_multiplier && (
                        <TooltipProvider>
                          <Tooltip content={`We double this and display it as ${allowance.granular_dependent_multiplier * 2}% to the Employer. This is because dependent/child plans are typically half the price (so increasing the allowance by ${allowance.granular_dependent_multiplier}% for each additional dependent will cover ${allowance.granular_dependent_multiplier * 2}% of the additional plan costs).`}>
                            <InfoIcon className='w-4 h-4 text-gray-500' />
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </td>
                </>
              )}
              <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                {getFormattedDate(allowance.updated_at, { month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit', second: '2-digit', timeZone: 'UTC' })} UTC
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FlatAllowancesSummary ({ allowanceSummary } : { allowanceSummary: AdminEmployerICHRAConfigAllowanceStructure }) {
  const groupedAllowances = []
  if (allowanceSummary.flat_allowances && !allowanceSummary.show_household_size_column) {
    for (let i = 0; i < allowanceSummary.flat_allowances.length; i += 3) {
      const chunk = allowanceSummary.flat_allowances.slice(i, i + 3)
      groupedAllowances.push(chunk)
    }
  }
  return (
    <div className='overflow-x-auto overflow-y-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg mt-2'>
      <table className='table-auto w-full divide-y divide-gray-300' data-testid='flat_allowances'>
        <thead className='bg-gray-50'>
          <tr>
            {allowanceSummary.show_household_size_column && (
              <>
                <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
                  <div className='flex justify-start'>
                    Household Size
                  </div>
                </th>
                <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
                  <div className='flex justify-start'>
                    Amount
                  </div>
                </th>
              </>
            )}
            {!allowanceSummary.show_household_size_column && (
              <>
                <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
                  <div className='flex justify-start'>
                    Location
                  </div>
                </th>
                {allowanceSummary.include_custom_columns && (
                  <>
                    <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
                      <div className='flex justify-start'>
                        Employment
                      </div>
                    </th>
                    <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
                      <div className='flex justify-start'>
                        Pay type
                      </div>
                    </th>
                    <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
                      <div className='flex justify-start'>
                        Age range
                      </div>
                    </th>
                  </>
                )}
                <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
                  <div className='flex justify-start'>
                    Employee
                  </div>
                </th>
                <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
                  <div className='flex justify-start'>
                    Spouse
                  </div>
                </th>
                <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
                  <div className='flex justify-start'>
                    Dependents
                  </div>
                </th>
                {allowanceSummary.include_custom_columns && (
                  <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
                    <div className='flex justify-start'>
                      Max dep. #
                    </div>
                  </th>
                )}
              </>
            )}
            <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
              <div className='flex justify-start'>
                QME Treatment
              </div>
            </th>
            <th className='whitespace-nowrap px-3 sm:px-2 md:px-4 py-3 sm:py-3 text-sm font-semibold text-gray-900 text-left'>
              <div className='flex justify-start'>
                Updated at
              </div>
            </th>
          </tr>
        </thead>
        <tbody className='divide-y divide-gray-200 bg-white'>
          {allowanceSummary.show_household_size_column && allowanceSummary.flat_allowances && allowanceSummary.flat_allowances.map((allowance) => (
            <tr
              className='odd:bg-white even:bg-gray-50 hover:cursor-pointer' key={allowance.id} onClick={() => {
                window.open(
                  `/admin/inspect/${allowance.id}`,
                  '_blank',
                  'noopener noreferrer'
                )
              }}
            >
              <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                {allowance.household_size || '(not set)'}
              </td>
              <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                {allowance.amount.formatted_amount}
              </td>
              <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                {allowance.qme_treatment || '(not set)'}
              </td>
              <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                {getFormattedDate(allowance.updated_at, { month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit', second: '2-digit', timeZone: 'UTC' })} UTC
              </td>
            </tr>
          ))}

          {!allowanceSummary.show_household_size_column && groupedAllowances && groupedAllowances.map((allowances) => (
            <tr className='odd:bg-white even:bg-gray-50' key={allowances[0].id}>
              <td className='whitespace-nowrap pl-6 py-3 text-sm text-gray-500'>{allowances[0].location_id || 'Default'}</td>
              {allowanceSummary.include_custom_columns && (
                <>
                  <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                    {allowances[0].employment_type || 'Default'}
                  </td>
                  <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                    {allowances[0].pay_type || 'Default'}
                  </td>
                  <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                    {allowances[0].min_age || 'n/a'} - {allowances[0].max_age || 'n/a'}
                  </td>
                </>
              )}
              {allowances.map((allowance) => (
                <td
                  className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left hover:cursor-pointer' key={allowance.id} onClick={() => {
                    window.open(
                  `/admin/inspect/${allowance.id}`,
                  '_blank',
                  'noopener noreferrer'
                    )
                  }}
                >
                  {allowance.amount.formatted_amount}
                </td>
              ))}
              {allowanceSummary.include_custom_columns && (
                <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                  {allowances[2].max_dependent_count || 'n/a'}
                </td>
              )}
              <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                {allowances[0].qme_treatment || '(not set)'}
              </td>
              <td className='whitespace-nowrap px-3 sm:px-4 py-3 sm:py-3 text-sm text-gray-700 text-left'>
                {getFormattedDate(allowances[0].updated_at, { month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit', second: '2-digit', timeZone: 'UTC' })} UTC
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function EmployerAllowancesSummaryImpl ({ ichraConfigId }: {
  ichraConfigId: string
}) {
  const { data: allowanceSummary, isLoading, isError } = useGetAllowanceSummary(ichraConfigId)

  if (isLoading) {
    return (
      <div className='bg-white p-4 rounded-md shadow-sm mb-4'>
        <p>Loading...</p>
      </div>
    )
  }

  if (isError || allowanceSummary === undefined) {
    return (
      <div className='bg-white p-4 rounded-md shadow-sm mb-4'>
        <p className='text-red-500'>Error loading allowance details</p>
      </div>
    )
  }

  return (
    <>
      <p className='helper-text'>Number of allowances defined: <strong>{allowanceSummary.total_allowance_count || 0}</strong></p>
      {allowanceSummary.include_custom_columns
        ? (
          <p className='font-semibold text-red-500'>
            This employer has custom allowances for this year - make sure to use the&nbsp;
            <TextLink href={`https://metabase.thatch.ai/question/1207-allowance-lookup-by-eic?employer_ichra_config_id=${allowanceSummary.ichra_config_id}`} color='purple' target='_blank' rel='noopener noreferrer'>Formatted Allowances</TextLink>
            &nbsp;link to see their full allowances instead of relying on the below table.
          </p>
          )
        : (
          <p>
            <TextLink href={`https://metabase.thatch.ai/question/1207-allowance-lookup-by-eic?employer_ichra_config_id=${allowanceSummary.ichra_config_id}`} color='purple' target='_blank' rel='noopener noreferrer'>Formatted Allowances</TextLink>
            &nbsp;(Metabase query showing allowances)
          </p>
          )}

      {allowanceSummary.granular_allowance_count > 0 && (
        <>
          <h4 className='text-lg font-bold leading-tight text-gray-900 mt-6'>
            Granular Allowances
          </h4>
          <GranularAllowancesSummary allowanceSummary={allowanceSummary} />
          {allowanceSummary.granular_allowance_count > 20 && (
            <p className='helper-text'>Truncated, displaying first 20 records. Use the linked Metabase query (Formatted allowances) to see all allowances.</p>
          )}
        </>
      )}

      {allowanceSummary.flat_allowance_count > 0 && (
        <>
          <h4 className='text-lg font-bold leading-tight text-gray-900 mt-6'>
            Flat Allowances
          </h4>
          <FlatAllowancesSummary allowanceSummary={allowanceSummary} />
          {allowanceSummary.flat_allowance_count > 20 && (
            <p className='helper-text'>Truncated, displaying first 20 records. Use the linked Metabase query (Formatted allowances) to see all allowances.</p>
          )}
        </>
      )}
    </>
  )
}

const EmployerAllowancesSummary = ({ ichraConfigId }: {
  ichraConfigId: string
}) => {
  return (
    <div id='allowances'>
      <h3 className='text-xl font-bold leading-tight text-gray-900'>
        Allowances
      </h3>
      <EmployerAllowancesSummaryImpl ichraConfigId={ichraConfigId} />
    </div>
  )
}

export default EmployerAllowancesSummary
